import React from "react";
import {makeStyles} from '@mui/styles';
import {
    AppBar,
    Avatar,
    Box,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery
} from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HelpIcon from '@mui/icons-material/Help';
import LoginIcon from "@mui/icons-material/Login";
import {Link, useHistory} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {useTranslation} from 'react-i18next';
import Title from "../../Title/Title";
import ThemeButton from "../../ThemeButton/ThemeButton";
import LanguageButton from "../../LanguageButton/LanguageButton";
import {rolesState, subState, tokenState} from "../../../services/requestService";
import PortalDialog from "../../PortalDialog/PortalDialog";
import {AnimatedIconButtonWithLabel, IconButtonWithLabel} from "../../general/button/IconButtonWithLabel";
import {ColorModeContext} from "../../../App";
import {useTheme} from "@emotion/react";
import Brightness7Icon from "@mui/icons-material/Brightness6";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import LanguageIcon from "@mui/icons-material/Language";
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SearchDialog, {SearchInput} from "../../Search/SearchDialog";

import ContextMenuItemLabel from "../../general/menuItem/ContextMenuItemLabel";
import {VERSION} from "../../../constants";

const useStyles = makeStyles((theme) => ({
    title: {
        textAlign: "left",
        marginLeft: theme.spacing(5),
    },
    link: {
        textDecoration: "none",
        color: theme.palette.text.primary,
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    divider: {
        background: theme.palette.appBar.dark,
        padding: 2,
    },
}));

const menuPaperProps = {
    elevation: 0,
    sx: {
        overflow: 'visible',
        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
        mt: 1.5,
        '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
        },
        '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
        },
    },
};

function Navbar() {
    const classes = useStyles();
    const history = useHistory();
    const {t, i18n} = useTranslation('common');
    const sub = useRecoilValue(subState);
    const [token, setToken] = useRecoilState(tokenState);
    const [roles, setRoles] = useRecoilState(rolesState);
    const hidden = useMediaQuery(theme => theme.breakpoints.up('md'));
    const [openLoginDialog, setOpenLoginDialog] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState("");
    const [openSearch, setOpenSearch] = React.useState(false);
    const colorMode = React.useContext(ColorModeContext);
    const theme = useTheme();
    /* More menu */
    const [anchorEl, setAnchorEl] = React.useState(null);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogin = () => {
        setOpenLoginDialog(true);
    };

    /* Logout */
    const handleLogout = () => {
        setToken(null);
        setRoles([]);
        history.replace("/portal");
        handleClose();
    };

    const toggleLanguage = () => {
        handleClose();
        let key = i18n.language.startsWith("en") ? 'fr' : 'en';
        i18n.changeLanguage(key);
    };

    // Search
    const handleChangeSearchValue = (event) => {
        setSearchValue(event.target.value);
    }

    const handleSubmit = () => {
        setOpenSearch(true);
    }

    const handleCloseSearch = () => {
        setOpenSearch(false);
        setSearchValue("");
    }

    return (
        <>
            <PortalDialog open={openLoginDialog} onClose={() => setOpenLoginDialog(false)}/>
            <AppBar
                sx={{
                    backgroundColor: theme.palette.appBar.main,
                    borderBottom: theme => theme.palette.mode === "light"
                        ? `solid 4px ${theme.palette.appBar.dark}`
                        : `solid 4px ${theme.palette.primary.dark}`,
                }}
                elevation={0}
                position="relative">
                {hidden && <Stack direction="row">
                    <div className={classes.title}>
                        <Link to={"/"} className={classes.link}>
                            <Title
                                title={t('main.title')}
                                variant="h2"
                                subtitle={t('main.subtitle')}
                                version={VERSION}
                                textAlign={'flex-start'}
                                color="#fff"/>
                        </Link>
                    </div>
                    <Box flexGrow={1}/>
                    <Toolbar>
                        <Stack direction="row" spacing={3} mr={1}>
                            <SearchInput value={searchValue} onChange={handleChangeSearchValue}
                                         onSubmit={handleSubmit}/>
                            <Divider orientation="vertical" flexItem/>
                        </Stack>
                        <LanguageButton color="inherit"/>
                        <ThemeButton color="inherit"/>
                        <AnimatedIconButtonWithLabel
                            onClick={() => history.push("/help")}
                            label={t('main.components.button.help')}
                            color="inherit"
                        >
                            <HelpIcon/>
                        </AnimatedIconButtonWithLabel>
                        <>
                            {token && sub === "readonly" ?
                                <IconButtonWithLabel
                                    onClick={handleLogin}
                                    label={t('main.components.button.login')}
                                    color="inherit"
                                >
                                    <LoginIcon/>
                                </IconButtonWithLabel>
                                : <Box>
                                    <Avatar
                                        onClick={handleClick}
                                        sx={{
                                            bgcolor: theme => theme.palette.secondary.main,
                                            color: theme => theme.palette.common.white,
                                            cursor: "pointer"
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {sub && sub.substr(0, 3).toUpperCase()}
                                        </Typography>
                                    </Avatar>
                                    <Menu
                                        id="connection-menu"
                                        anchorEl={anchorEl}
                                        keepMounted
                                        open={Boolean(anchorEl)}
                                        onClose={handleClose}
                                        PaperProps={menuPaperProps}
                                        transformOrigin={{horizontal: 'right', vertical: 'top'}}
                                        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                                    >
                                        {sub !== "anonymous" &&
                                            <MenuItem onClick={handleClose} component={Link} to={'/profile'}>
                                                <ContextMenuItemLabel
                                                    icon={<AccountCircleIcon fontSize="small"/>}
                                                >
                                                    {t('main.components.button.profile')}
                                                </ContextMenuItemLabel>
                                            </MenuItem>}
                                        {roles.includes("ROLE_ADMIN") && (
                                            <MenuItem onClick={handleClose} component={Link} to={'/admin'}>
                                                <ContextMenuItemLabel
                                                    icon={<AdminPanelSettingsIcon fontSize="small"/>}
                                                >
                                                    Admin
                                                </ContextMenuItemLabel>
                                            </MenuItem>
                                        )}
                                        <MenuItem onClick={handleLogout}>
                                            <ContextMenuItemLabel
                                                icon={<LogoutIcon fontSize="small"/>}
                                            >
                                                {t('main.components.button.logout')}
                                            </ContextMenuItemLabel>
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            }
                        </>
                    </Toolbar>
                </Stack>}
                {!hidden && <Stack direction="row">
                    <Box flexGrow={1}>
                        <Link to={"/"} className={classes.link}>
                            <Title
                                title={t('main.title')}
                                variant="h3"
                                subtitle={t('main.subtitle')}
                                version={VERSION}
                                subVariant="subtitle2"
                                textAlign={'center'}
                                color="#fff"/>
                        </Link>
                    </Box>
                    <Toolbar sx={{position: "absolute", right: 0, top: 17}}>
                        <IconButton
                            aria-controls="simple-menu"
                            aria-haspopup="true"
                            onClick={() => setOpenSearch(true)}
                            edge="end"
                            color="inherit">
                            <SearchIcon/>
                        </IconButton>
                        <Tooltip title={t('main.components.button.more')} arrow>
                            <IconButton
                                aria-controls="simple-menu"
                                aria-haspopup="true"
                                onClick={handleClick}
                                edge="end"
                                color="inherit">
                                <MoreVertIcon/>
                            </IconButton>
                        </Tooltip>
                    </Toolbar>
                    <Menu
                        id="simple-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        PaperProps={menuPaperProps}
                        transformOrigin={{horizontal: 'right', vertical: 'top'}}
                        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    >
                        {token && sub !== "readonly" && sub !== "anonymous"
                            && <MenuItem onClick={handleClose} component={Link} to={'/profile'}>
                                <ContextMenuItemLabel
                                    icon={<AccountCircleIcon fontSize="small"/>}
                                >
                                    {t('main.components.button.profile')}
                                </ContextMenuItemLabel>
                            </MenuItem>}
                        {roles.includes("ROLE_ADMIN") && (
                            <MenuItem onClick={handleClose} component={Link} to={'/admin'}>
                                <ContextMenuItemLabel
                                    icon={<AccountCircleIcon fontSize="small"/>}
                                >
                                    Admin
                                </ContextMenuItemLabel>
                            </MenuItem>
                        )}
                        <MenuItem onClick={toggleLanguage}>
                            <ContextMenuItemLabel
                                icon={<LanguageIcon fontSize="small"/>}
                            >
                                {i18n.language.startsWith("en") ? "FR" : "EN"}
                            </ContextMenuItemLabel>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            handleClose();
                            colorMode.toggleColorMode();
                        }}>
                            <ContextMenuItemLabel
                                icon={theme.palette.mode === 'dark'
                                    ? <Brightness7Icon fontSize="small"/>
                                    : <Brightness4Icon fontSize="small"/>}
                            >
                                {t('main.components.button.theme')}
                            </ContextMenuItemLabel>
                        </MenuItem>
                        <MenuItem onClick={handleClose} component={Link} to={'/help'}>
                            <ContextMenuItemLabel
                                icon={<HelpIcon fontSize="small"/>}
                            >
                                {t('main.components.button.help')}
                            </ContextMenuItemLabel>
                        </MenuItem>
                        {token && sub === "readonly"
                            ? <MenuItem onClick={() => {
                                handleClose();
                                handleLogin();
                            }}>
                                <ContextMenuItemLabel
                                    icon={<LoginIcon fontSize="small"/>}
                                >
                                    {t('main.components.button.login')}
                                </ContextMenuItemLabel>
                            </MenuItem>
                            : <MenuItem onClick={handleLogout}>
                                <ContextMenuItemLabel
                                    icon={<LogoutIcon fontSize="small"/>}
                                >
                                    {t('main.components.button.logout')}
                                </ContextMenuItemLabel>
                            </MenuItem>
                        }
                    </Menu>
                </Stack>}
            </AppBar>
            <SearchDialog
                open={openSearch}
                onClose={handleCloseSearch}
                value={searchValue}
                onChange={(event, newValue) => setSearchValue(newValue)}
            />
        </>
    );
}

export default Navbar;